import { TFunction } from 'i18next';
import React from 'react';

import uiHelper from '../../../helpers/ui.helper';
import { PriceDataPoint } from '../../../types';
import { DEFAULT_SERIES_HEIGHT } from '../modules/constants';
import { TooltipEntry } from '../modules/tooltips';

export type SeriesGenerator = DataSetSeriesGenerator | ExternalSeriesGenerator;

export type ChartGenerator = ExternalMarkerGenerator | SeriesGenerator;

/**
  ____            _           
 / ___|  ___ _ __(_) ___  ___ 
 \___ \ / _ \ '__| |/ _ \/ __|
  ___) |  __/ |  | |  __/\__ \
 |____/ \___|_|  |_|\___||___/
                              
 */
abstract class SeriesBase {
  abstract TYPE: string;
  /**
   * isSeparateFromPriceChart controls whether it shares the chart canvas
   * as the OLHC candlesticks.
   */
  abstract isSeparateFromPriceChart: boolean;

  /**
   * The chart name is used for creating chart titles.
   */
  abstract getChartName(): string;

  /**
   * The yAxis formatter, return a tooltip formatter
   */
  yAxisFormatter:
    | string
    | ((value: number, index: number) => string)
    | undefined;

  /**
   * shareYAxisWithPriceChart controls whether the series should share the same
   * y-axis as the OLHC candlesticks.
   * It is only applicable when isSeparateFromPriceChart is false.
   */
  shareYAxisWithPriceChart = false;

  /**
   * The height of the series, only works if shareYAxisWithPriceChart is false
   * If it is set to -1, it is the same height as price chart
   */
  height = DEFAULT_SERIES_HEIGHT;

  /**
   * y axis labels are hidden automatically if isSeparateFromPriceChart is false,
   * set this to true to force it to be visible.
   */
  forceShowYAxisLabels = false;

  /**
   * y axis are scaled automatically so that it doesn't start from 0,
   * set this to true to force it not to scale.
   */
  forceYAxisNotToScale = false;

  t: TFunction;

  /**
   * Allow a generator to provide a visual map
   * https://echarts.apache.org/en/option.html#visualMap-piecewise
   */
  visualMap?: () => echarts.EChartOption.VisualMap;

  constructor(t: TFunction) {
    this.t = t;
  }
}

export type TooltipFormatter = (
  formatObject: echarts.EChartOption.Tooltip.Format
) => TooltipEntry[];

/**
 * We only care about a subset of series types
 * Two axisIndex are assigned by price chart viewer in late pipeline,
 * tooltip won't work because we have global tooltip settings,
 * name is assigned through getSeriesName().
 */
type _OmittedProperties = 'xAxisIndex' | 'yAxisIndex' | 'tooltip' | 'name';
export interface PriceViewerSeries {
  name: string;
  echartSeries:
    | Omit<echarts.EChartOption.SeriesLine, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesBar, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesScatter, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesEffectScatter, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesCandlestick, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesLines, _OmittedProperties>
    | Omit<echarts.EChartOption.SeriesCustom, _OmittedProperties>;
  legendColor?: string;
  legendTextField?: string;
  legendFormat?: string;
  tooltipFormatter: TooltipFormatter;
}

export abstract class DataSetSeriesGenerator extends SeriesBase {
  TYPE = 'DataSetSeriesGenerator';
  abstract getSeries(
    dataPoints: ReadonlyArray<PriceDataPoint>
  ): PriceViewerSeries | PriceViewerSeries[];
}
export abstract class ExternalSeriesGenerator extends SeriesBase {
  TYPE = 'ExternalSeriesGenerator';
  abstract getSeries(
    dataPoints: ReadonlyArray<PriceDataPoint>
  ): PriceViewerSeries | PriceViewerSeries[];
}

export const isDataSetSeriesGenerator = (
  generator: SeriesGenerator
): generator is DataSetSeriesGenerator => {
  return generator.TYPE === 'DataSetSeriesGenerator';
};

export const isExternalSeriesGenerator = (
  generator: SeriesGenerator
): generator is ExternalSeriesGenerator => {
  return generator.TYPE === 'ExternalSeriesGenerator';
};

export const isSeriesGenerator = (
  generator: ChartGenerator
): generator is SeriesGenerator => {
  return generator.TYPE.endsWith('SeriesGenerator');
};
/**
  __  __            _                 
 |  \/  | __ _ _ __| | _____ _ __ ___ 
 | |\/| |/ _` | '__| |/ / _ \ '__/ __|
 | |  | | (_| | |  |   <  __/ |  \__ \
 |_|  |_|\__,_|_|  |_|\_\___|_|  |___/
 */
export type TooltipCreator = (
  name: string,
  coord: [string, number]
) => React.ReactElement;

export interface ExternalMarkerGeneratorGetMarkers {
  data: echarts.EChartOption.SeriesCandlestick['markPoint']['data'][];
  tooltipCreater: TooltipCreator;
}
export abstract class ExternalMarkerGenerator {
  TYPE = 'ExternalMarkerGenerator';
  abstract getMarkers(
    dataPoints: ReadonlyArray<PriceDataPoint>,
    markPointWidth: number
  ): ExternalMarkerGeneratorGetMarkers;
}

export const isExternalMarkerGenerator = (
  generator: ChartGenerator
): generator is ExternalMarkerGenerator => {
  return generator.TYPE === 'ExternalMarkerGenerator';
};

export const formatAsNumber = (
  value: number | undefined | null
): string | undefined | null => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  return value.toLocaleString('en-US');
};

const formatAsPercentage = (
  value: number | undefined | null
): string | undefined | null => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  return uiHelper.formatPercentage(value);
};

/**
 *
 * @param option
 *  '1d': if the data is an 1d array;
 *  '2d': if the data is a 2d array: which looks like: [<date>, value][].
 *  'theThirdValue : if the data is a 2d array: which looks like: [<date>, value, value][].
 *  '{accessor: string}: if the data is directly from encoding.
 * @param format 'number' | 'percentage'
 * @param color 'string', optional, control the legend color
 * @returns
 */

export interface ColorOption {
  keyColor?: string;
  valueColor?: string;
  shouldChangeColorByValue?: boolean;
}
export const getDefaultToolptipFormatter = (
  option: '1d' | '2d' | '3x2d' | { accessor: string },
  format: 'number' | 'percentage',
  colorOption?: ColorOption
): TooltipFormatter => {
  return (
    formatObject: echarts.EChartOption.Tooltip.Format
  ): TooltipEntry[] => {
    const formatter = format === 'number' ? formatAsNumber : formatAsPercentage;
    let formatted: string | undefined | null;
    switch (option) {
      case '1d': {
        // the data is provided as "data" field, so we can directly get the value from there.
        formatted = formatter(formatObject.data);
        break;
      }
      case '2d': {
        // the 1st element is date, end element is data.
        formatted = formatter(formatObject.data[1]);
        break;
      }
      case '3x2d': {
        // the 1st element is date, end element is data.
        formatted = formatter(formatObject.data[2]);
        break;
      }
      default: {
        formatted = formatter(formatObject.data[option.accessor]);
      }
    }
    if (
      colorOption.shouldChangeColorByValue !== undefined &&
      colorOption.shouldChangeColorByValue
    ) {
      colorOption.keyColor = Number.parseInt(formatted) > 0 ? 'green' : 'red';
    }
    return [
      {
        name: formatObject.seriesName,
        value: formatted,
        colorOption,
      },
    ];
  };
};

/**
 * This tooltip formatter can be directly used on series if the data is a 2d array:
 * which looks like: [<date>, value][].
 */
export const defaultTooltipForSeriesUsing2DimensionData: TooltipFormatter =
  getDefaultToolptipFormatter('2d', 'number', { keyColor: '' });
